import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createTransaction, getStreamerCards, getCreatorInfo } from "../api/apis"; // API calls
import ProtectedRoute from "../Components/ProtectedRoute";
import { processCashfreePayment } from '../utils/cashfree';
import { toast } from "react-toastify";
import { ShoppingCart } from 'lucide-react';

const StreamerCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { username: urlUsername } = useParams();
  // Resolve streamer target
  const searchParams = new URLSearchParams(location.search);
  const qpStreamer = searchParams.get('streamerEmailId');
  const qpUsername = searchParams.get('username');
  // Always prioritize the URL param username for identity of the page
  const streamerUsername = urlUsername || qpUsername || location.state?.username || undefined;
  const ssStreamer = typeof window !== 'undefined' ? sessionStorage.getItem('lastStreamerEmailId') : null;
  // Only prefill email when URL does not specify a username; otherwise force fresh resolution
  const initialStreamerEmailId = urlUsername ? null : (location.state?.streamerEmailId || qpStreamer || ssStreamer);
  const [resolvedStreamerEmail, setResolvedStreamerEmail] = useState(initialStreamerEmailId);
  const [resolvedForUsername, setResolvedForUsername] = useState(urlUsername || qpUsername || null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  // FontAwesome CDN for icons
  useEffect(() => {
    if (!document.getElementById("fa-cdn")) {
      const link = document.createElement("link");
      link.id = "fa-cdn";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css";
      link.crossOrigin = "anonymous";
      link.referrerPolicy = "no-referrer";
      document.head.appendChild(link);
    }
  }, []);

  // Resolve streamerEmailId via username when arriving through shareable URL
  useEffect(() => {
    const resolveByUsername = async () => {
      try {
        if (urlUsername) {
          const resp = await getCreatorInfo(urlUsername);
          if (resp?.data?.status === 'success' && resp.data?.data?.emailId) {
            setResolvedStreamerEmail(resp.data.data.emailId);
            setResolvedForUsername(urlUsername);
            setCreatorInfo(resp.data.data); // Store creator info including social links
            try {
              sessionStorage.setItem('lastStreamerEmailId', resp.data.data.emailId);
              sessionStorage.setItem('lastStreamerUsername', urlUsername);
            } catch { }
          } else {
            navigate('/streamers');
          }
        }
      } catch (e) {
        console.error('Failed to resolve creator by username', e);
        navigate('/streamers');
      }
    };
    resolveByUsername();
  }, [urlUsername, navigate]);

  const checkTimeout = () => {
    const expirationTime = localStorage.getItem("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      localStorage.clear();
      return false;
    }
    return true;
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.error("Session expired. Please log in again.");
    navigate("/login");
  };

  useEffect(() => {
    if (!checkTimeout()) {
      handleLogout();
      return;
    }
    if (!resolvedStreamerEmail) {
      // If arriving via shareable URL with :username, wait for resolution instead of redirecting
      if (urlUsername) {
        return;
      }
      navigate("/streamers");
      return;
    }
    // If we are on a username-specific page but the resolved email is for a different username, wait
    if (urlUsername && resolvedForUsername !== urlUsername) {
      return;
    }
    // Reset UI while fetching new streamer's cards to avoid showing stale data
    setCards([]);
    setLoading(true);
    const fetchStreamerCards = async () => {
      try {
        const headers = {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Info": user,
          },
        };
        const response = await getStreamerCards(resolvedStreamerEmail, headers);
        if (response.data.status === "success") {
          const fetchedCards = response.data.cards || [];
          setCards(fetchedCards);
          setLoading(false);
        } else {
          toast.error(response.data.error || "Failed to fetch cards.");
        }
      } catch (error) {
        console.error("Error fetching streamer cards:", error);
      }
    };

    fetchStreamerCards();
  }, [resolvedStreamerEmail, urlUsername]);

  const handleBuyCard = async (card) => {
    try {
      console.log('🛒 Purchase initiated for card:', card.cardTitle);
      console.log('📧 Streamer Email ID:', card.streamerEmailId);
      console.log('👤 Streamer Username:', streamerUsername);

      // Enhance card with streamer username
      const enhancedCard = {
        ...card,
        streamerUsername: streamerUsername || card.username
      };

      // Persist streamer for post-payment redirect
      try {
        sessionStorage.setItem('lastStreamerEmailId', card.streamerEmailId || resolvedStreamerEmail || '');
        if (streamerUsername) {
          sessionStorage.setItem('lastStreamerUsername', streamerUsername);
        }
      } catch { }

      // Check session before purchase
      if (!checkTimeout()) {
        handleLogout();
        return;
      }

      // Convert price to number and check if it's 0
      const price = Number(card.price);

      // For free cards, create transaction directly without any payment process
      if (price === 0) {
        const transactionData = {
          cardId: card._id,
          streamerEmailId: card.streamerEmailId,
          userEmailId: user?.emailId || user?.email,
          title: card.cardTitle,
          description: card.description,
          isUsed: true,
          purchaseSource: 'streamer-page',
          paymentId: 'free',
          orderId: 'free'
        };

        console.log('💳 Creating transaction with data:', transactionData);

        const transactionResponse = await createTransaction(transactionData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'User-Info': user?.emailId || user?.username || user
          }
        });

        console.log('✅ Transaction response:', transactionResponse.data);

        if (transactionResponse.data?.status === 'success') {
          toast.success('Card purchased successfully!');
          console.log('🎉 Transaction created successfully!');
          // Stay on streamer cards page
        } else {
          console.error('❌ Transaction failed:', transactionResponse.data);
          throw new Error(transactionResponse.data?.message || 'Failed to create transaction');
        }
        return;
      }

      console.log('💰 Processing paid card purchase');
      // Process payment with Cashfree
      await processCashfreePayment(
        enhancedCard,
        async (data) => {
          try {
            console.log('🎉 ========== PAYMENT SUCCESS CALLBACK ==========');
            console.log('💰 Payment completed successfully!');
            console.log('📦 Payment data received:', JSON.stringify(data, null, 2));

            // Get card data from callback (passed from cashfree.js)
            const cardData = data.cardData || card;
            console.log('🎴 Using card data:', JSON.stringify(cardData, null, 2));

            // Ensure we have all required fields
            if (!cardData._id) {
              throw new Error('Card ID is missing');
            }
            if (!cardData.streamerEmailId) {
              throw new Error('Streamer email is missing');
            }
            if (!user?.emailId && !user?.email) {
              throw new Error('User email is missing');
            }

            const transactionData = {
              cardId: cardData._id,
              streamerEmailId: cardData.streamerEmailId,
              userEmailId: user?.emailId || user?.email,
              title: cardData.cardTitle,
              description: cardData.description,
              isUsed: true,
              purchaseSource: 'streamer-page',
              paymentId: data.payment_id || data.order_id || `payment_${Date.now()}`,
              orderId: data.order_id || `order_${Date.now()}`
            };

            console.log('💳 Creating transaction with data:', JSON.stringify(transactionData, null, 2));
            console.log('🔑 Token:', token ? 'EXISTS' : 'MISSING');
            console.log('👤 User:', JSON.stringify(user, null, 2));

            const transactionResponse = await createTransaction(transactionData, {
              headers: {
                Authorization: `Bearer ${token}`,
                'User-Info': user?.emailId || user?.username || user
              }
            });

            console.log('✅ Transaction API response:', JSON.stringify(transactionResponse, null, 2));

            if (transactionResponse.data?.status === 'success') {
              console.log('🎉 ✅ TRANSACTION CREATED SUCCESSFULLY!');
              console.log('🎫 Transaction ID:', transactionResponse.data.transactionId);
              toast.success('Card purchased successfully!');

              // Clear pending card data
              sessionStorage.removeItem('pendingCardPurchase');
              delete window.__pendingCardPurchase;

              console.log('🟢 Transaction complete. Card should appear on overlay within 5 seconds.');
              // Ensure redirect back to this page after hosted checkout (safety)
              try { sessionStorage.setItem('lastStreamerEmailId', cardData.streamerEmailId || ''); } catch { }
            } else {
              console.error('❌ Transaction API returned non-success:', transactionResponse.data);
              throw new Error(transactionResponse.data?.message || 'Transaction creation failed');
            }
          } catch (error) {
            console.error('❌ ========== ERROR IN PAYMENT SUCCESS CALLBACK ==========');
            console.error('❌ Error:', error);
            console.error('❌ Error message:', error.message);
            console.error('❌ Error stack:', error.stack);
            if (error.response) {
              console.error('❌ API Error Response:', JSON.stringify(error.response.data, null, 2));
            }
            toast.error(`❌ Payment completed but transaction failed: ${error.message}`);
          }
        },
        (error) => {
          console.error('❌ ========== PAYMENT ERROR CALLBACK ==========');
          console.error('Error:', error);
          toast.error(error.message || 'Payment failed. Please try again.');
        }
      );
    } catch (error) {
      toast.error(error.message || 'Payment process failed');
    }
  };

  const handleOpenModal = (card) => {
    setSelectedCard(card);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
    setShowModal(false);
  };

  const handleOpenCardModal = (card) => {
    setModalCard(card);
  };

  const handleCloseCardModal = () => {
    setModalCard(null);
  };

  const getCardColor = (rarity) => {
    switch (rarity.toLowerCase()) {
      case "common":
        return "bg-gradient-to-br from-gray-700/70 to-gray-500/40";
      case "rare":
        return "bg-gradient-to-br from-blue-700/70 to-blue-500/40";
      case "epic":
        return "bg-gradient-to-br from-purple-700/70 to-purple-500/40";
      case "legendary":
        return "bg-gradient-to-br from-orange-700/70 to-orange-500/40";
      default:
        return "bg-gradient-to-br from-gray-800/70 to-gray-600/40";
    }
  };

  const getRarityBadgeColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case "common":
        return "bg-gray-500 text-white";
      case "rare":
        return "bg-blue-500 text-white";
      case "epic":
        return "bg-purple-500 text-white";
      case "legendary":
        return "bg-orange-500 text-black";
      default:
        return "bg-yellow-400 text-neutral-950";
    }
  };

  return (
    <div className="bg-neutral-950 flex flex-col min-h-screen font-sans antialiased text-neutral-50">
      <main className="flex-1 pt-6">
        <section className="py-10 px-6 md:px-14 bg-gradient-to-br from-black via-neutral-950 to-neutral-900">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-2 text-center bg-gradient-to-r from-yellow-400 via-yellow-200 to-yellow-300 bg-clip-text text-transparent">
              Streamer Cards
            </h1>
            {creatorInfo && (
              <div className="flex justify-center items-center gap-4 mb-6">
                {creatorInfo.youtubeLink && (
                  <a
                    href={creatorInfo.youtubeLink.startsWith('http') ? creatorInfo.youtubeLink : `https://youtube.com/${creatorInfo.youtubeLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-500 hover:text-red-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fab fa-youtube text-2xl"></i>
                  </a>
                )}
                {creatorInfo.instagramLink && (
                  <a
                    href={creatorInfo.instagramLink.startsWith('http') ? creatorInfo.instagramLink : `https://instagram.com/${creatorInfo.instagramLink.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-500 hover:text-pink-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fab fa-instagram text-2xl"></i>
                  </a>
                )}
              </div>
            )}
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-300 mx-auto mb-12"></div>

            {loading ? (
              <div className="text-center py-12">
                <i className="fa-solid fa-spinner fa-spin text-4xl text-yellow-400 mb-4"></i>
                <p className="text-xl text-yellow-100">Loading cards...</p>
              </div>
            ) : cards.length === 0 ? (
              <div className="text-center py-12">
                <i className="fa-solid fa-box-open text-6xl text-yellow-400 mb-6"></i>
                <p className="text-xl text-yellow-100 mb-4">No cards found</p>
                <p className="text-neutral-300">This streamer hasn't created any cards yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {cards.map((card, index) => (
                  <div
                    key={index}
                    className="card-animated relative p-6 rounded-2xl border-2 border-neutral-800 bg-gradient-to-br from-neutral-900/70 to-neutral-800/40 text-white flex flex-col justify-between shadow-lg group cursor-pointer"
                    onClick={() => handleOpenCardModal(card)}
                  >
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getRarityBadgeColor(card.rarity)}`}>
                          {card.rarity}
                        </span>
                        {card.price === 0 && (
                          <div className="bg-green-500/20 rounded-full px-2 py-1">
                            <span className="text-green-400 text-xs font-bold">FREE</span>
                          </div>
                        )}
                      </div>
                      <div className="relative mb-4 overflow-hidden rounded-xl">
                        <img
                          src={card.imageUrl}
                          alt={card.cardTitle}
                          className="w-full h-48 object-contain bg-black transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      </div>
                      <h2 className="text-xl font-bold mb-3 text-yellow-100">
                        {card.cardTitle}
                      </h2>
                      <p className="text-sm mb-4 text-neutral-300 line-clamp-2">{card.description}</p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-lg font-bold text-yellow-300">
                        <i className="fa-solid fa-coins text-yellow-400"></i>
                        <span>{card.price === 0 ? 'FREE' : `₹${card.price}`}</span>
                      </div>
                      <button
                        className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25 mt-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(card);
                        }}
                      >
                        <ShoppingCart className="w-5 h-5" />
                        <span>{card.price === 0 ? 'Get Free' : 'Purchase'}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Purchase Confirmation Modal */}
      {showModal && selectedCard && (
        <div className="fixed z-50 inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center p-6" onClick={handleCloseModal}>
          <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-yellow-500/30 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-3 right-3 text-yellow-300 hover:text-yellow-200 text-2xl font-bold w-8 h-8 flex items-center justify-center" onClick={handleCloseModal}>
              ×
            </button>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-80 md:h-full">
                <img src={selectedCard.imageUrl} alt={selectedCard.cardTitle} className="w-full h-full object-contain bg-black" />
                <div className="absolute inset-0 ring-1 ring-yellow-400/40 rounded-none"></div>
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getRarityBadgeColor(selectedCard.rarity)}`}>
                    Confirm Purchase
                  </span>
                </div>
                {selectedCard.price === 0 && (
                  <div className="absolute top-3 right-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-bold">
                    FREE
                  </div>
                )}
              </div>
              <div className="p-6 space-y-4">
                <h3 className="text-2xl font-bold text-yellow-300">{selectedCard.cardTitle}</h3>
                <p className="text-gray-300 text-sm">{selectedCard.description}</p>

                <div className="bg-neutral-800/50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Price:</span>
                    <span className="text-2xl font-bold text-yellow-300">
                      {selectedCard.price === 0 ? 'FREE' : `₹${selectedCard.price}`}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      handleBuyCard(selectedCard);
                      handleCloseModal();
                    }}
                    className="flex-1 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span>{selectedCard.price === 0 ? 'Get Free Card' : 'Confirm Purchase'}</span>
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="bg-neutral-700 hover:bg-neutral-600 px-6 py-3 rounded-full font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Modal */}
      {modalCard && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={handleCloseCardModal}>
          <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-yellow-500/30 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-3 right-3 text-yellow-300 hover:text-yellow-200 text-2xl font-bold w-8 h-8 flex items-center justify-center" onClick={handleCloseCardModal}>
              ×
            </button>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-80 md:h-full">
                <img src={modalCard.imageUrl || "https://picsum.photos/300/200"} alt={modalCard.cardTitle} className="w-full h-full object-contain bg-black" />
                <div className="absolute inset-0 ring-1 ring-yellow-400/40 rounded-none"></div>
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getRarityBadgeColor(modalCard.rarity)}`}>
                    {modalCard.rarity}
                  </span>
                </div>
                {modalCard.price === 0 && (
                  <div className="absolute top-3 right-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-bold">
                    FREE
                  </div>
                )}
              </div>
              <div className="p-6 space-y-4">
                <h3 className="text-2xl font-bold text-yellow-300">{modalCard.cardTitle}</h3>
                <p className="text-gray-300 text-sm">{modalCard.description}</p>

                <div className="space-y-3">

                  <div className="flex items-center space-x-2 text-lg font-bold text-yellow-300">
                    <i className="fa-solid fa-coins text-yellow-400"></i>
                    <span>{modalCard.price === 0 ? 'FREE' : `₹${modalCard.price}`}</span>
                  </div>
                </div>

                <button
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25 mt-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseCardModal();
                    handleOpenModal(modalCard);
                  }}
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>{modalCard.price === 0 ? 'Get Free' : 'Purchase'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        .card-animated {
          transition: all 0.25s cubic-bezier(0.6, 2, 0.4, 1);
        }
        .card-animated:hover {
          transform: translateY(-6px) scale(1.035);
          box-shadow: 0 8px 40px #ffd86633, 0 0 40px #fff2 inset;
          border-color: #ffd866;
        }
        .btn-futuristic {
          transition: all 0.3s cubic-bezier(0.45, 1.6, 0.4, 0.8);
          background: linear-gradient(90deg, #ffd866 80%, #ffe496 100%);
          color: #18170f;
          font-weight: 700;
          border: 2px solid #e3c162;
          text-shadow: 0 2px 12px #fff4;
        }
        .btn-futuristic:hover {
          background: linear-gradient(90deg, #e7c067 50%, #fff0b1 100%);
          color: #222;
          box-shadow: 0 0 24px #ffd86688, 0 2px 20px #e3c16255;
          transform: scale(1.04);
        }
        .shadow-futuristic {
          box-shadow: 0 0 80px 10px #ffd86633, 0 0 32px #ffd86622 inset;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default StreamerCards;
